import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import OpsLayout from "./components/ops/OpsLayout";
import OpsLegacyRedirect from "./components/ops/OpsLegacyRedirect";
import AdminModuleGate from "./components/ops/AdminModuleGate";
import { renderCustomerRoutes } from "./components/customer/CustomerRoutes";
import { renderCaregiverRoutes } from "./components/caregiver/CaregiverRoutes";
import { renderCareManagerRoutes } from "./components/caremanager/CareManagerRoutes";
import LandingPage from "./pages/LandingPage";
import { lazyPage } from "./components/shared/lazyPage";
import { LEGACY_ADMIN_REDIRECTS } from "./config/adminSopConfig";

const Login = lazyPage(() => import("./pages/Login"));
const PaymentPage = lazyPage(() => import("./pages/PaymentPage"));
const CarePassCheckout = lazyPage(() => import("./pages/CarePassCheckout"));
const CarePassSuccess = lazyPage(() => import("./pages/CarePassSuccess"));
const CarePassInvoice = lazyPage(() => import("./pages/CarePassInvoice"));
const BecomeCaregiverPage = lazyPage(() => import("./pages/BecomeCaregiverPage"));
const CareManagerRegistrationWizard = lazyPage(() => import("./pages/CareManagerRegistrationWizard"));
const PrivacyPolicyPage = lazyPage(() => import("./pages/PrivacyPolicyPage"));
const TermsOfUsePage = lazyPage(() => import("./pages/TermsOfUsePage"));
const SafetySecurityPage = lazyPage(() => import("./pages/SafetySecurityPage"));
const RefundPolicyPage = lazyPage(() => import("./pages/RefundPolicyPage"));
const GuestBookingWizard = lazyPage(() => import("./pages/GuestBookingWizard"));
const PublicCaregiverProfile = lazyPage(() => import("./pages/PublicCaregiverProfile"));
const OpsDashboardHome = lazyPage(() => import("./pages/OpsDashboardHome"));
const OpsVerificationQueue = lazyPage(() => import("./pages/OpsVerificationQueue"));
const OpsPoliceRecordVerification = lazyPage(() => import("./pages/OpsPoliceRecordVerification"));
const OpsPlatformSettings = lazyPage(() => import("./pages/OpsPlatformSettings"));
const AdminFeatureManagement = lazyPage(() => import("./pages/AdminFeatureManagement"));
const AdminSystemSettings = lazyPage(() => import("./pages/AdminSystemSettings"));
const AdminPlatformConfiguration = lazyPage(() => import("./pages/AdminPlatformConfiguration"));
const AdminNursingProcedureCatalog = lazyPage(() => import("./pages/AdminNursingProcedureCatalog"));
const AdminPhysioServiceCatalog = lazyPage(() => import("./pages/AdminPhysioServiceCatalog"));
const AdminWeeklyReport = lazyPage(() => import("./pages/AdminWeeklyReport"));
const OpsCallManagement = lazyPage(() => import("./pages/OpsCallManagement"));
const OpsBookingControls = lazyPage(() => import("./pages/OpsBookingControls"));
const AdminBookingManagement = lazyPage(() => import("./pages/AdminBookingManagement"));
const AdminShiftManagement = lazyPage(() => import("./pages/AdminShiftManagement"));
const AdminCareAgreements = lazyPage(() => import("./pages/AdminCareAgreements"));
const AdminCareManagerManagement = lazyPage(() => import("./pages/AdminCareManagerManagement"));
const AdminCustomerManagement = lazyPage(() => import("./pages/AdminCustomerManagement"));
const AdminNpidManagement = lazyPage(() => import("./pages/AdminNpidManagement"));
const AdminAttendantManagement = lazyPage(() => import("./pages/AdminAttendantManagement"));
const AdminNurseManagement = lazyPage(() => import("./pages/AdminNurseManagement"));
const AdminPhysiotherapistManagement = lazyPage(() => import("./pages/AdminPhysiotherapistManagement"));
const AdminMatchmaking = lazyPage(() => import("./pages/AdminMatchmaking"));
const AdminRequirementDetails = lazyPage(() => import("./pages/AdminRequirementDetails"));
const AdminUserRoleManagement = lazyPage(() => import("./pages/AdminUserRoleManagement"));
const AdminAuditCenter = lazyPage(() => import("./pages/AdminAuditCenter"));
const AdminIntegrations = lazyPage(() => import("./pages/AdminIntegrations"));
const OpsCaregiverProfiles = lazyPage(() => import("./pages/OpsCaregiverProfiles"));
const AdminCaregiverProfileEditor = lazyPage(() => import("./pages/AdminCaregiverProfileEditor"));
const OpsSopModulePage = lazyPage(() => import("./pages/OpsSopModulePage"));
const AdminSubscriptions = lazyPage(() => import("./pages/AdminSubscriptions"));
const AdminComplaintsTrust = lazyPage(() => import("./pages/AdminComplaintsTrust"));
const OpsCrmsPage = lazyPage(() => import("./pages/OpsCrmsPage"));
const OpsLeadCrmPage = lazyPage(() => import("./pages/OpsLeadCrmPage"));
const AdminReportsAnalytics = lazyPage(() => import("./pages/AdminReportsAnalytics"));
const AdminPaymentsFinance = lazyPage(() => import("./pages/AdminPaymentsFinance"));
const AdminDailyOperationsReport = lazyPage(() => import("./pages/AdminDailyOperationsReport"));
const AdminSupportTickets = lazyPage(() => import("./pages/AdminSupportTickets"));
const OpsCareHubConsole = lazyPage(() => import("./pages/OpsCareHubConsole"));
const NotificationsPage = lazyPage(() => import("./pages/NotificationsPage"));
const MessengerPage = lazyPage(() => import("./pages/MessengerPage"));

function adminModule(permission, element) {
  return <AdminModuleGate permission={permission}>{element}</AdminModuleGate>;
}

function LegacyOpsRedirect() {
  const { pathname, search } = useLocation();
  const next = pathname.replace(/^\/ops/, "/admin") || "/admin";
  return <Navigate to={`${next}${search}`} replace />;
}

function AppRoutes() {
  return (
    <>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/become-caregiver" element={<BecomeCaregiverPage />} />
        <Route path="/become-care-manager" element={<CareManagerRegistrationWizard />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-use" element={<TermsOfUsePage />} />
        <Route path="/safety-security" element={<SafetySecurityPage />} />
        <Route path="/refund-policy" element={<RefundPolicyPage />} />
        <Route path="/book" element={<GuestBookingWizard />} />
        <Route path="/pricing" element={<PaymentPage />} />
        <Route path="/pricing/checkout" element={<CarePassCheckout />} />
        <Route path="/pricing/pay" element={<Navigate to="/pricing/checkout" replace />} />
        <Route path="/pricing/success" element={<CarePassSuccess />} />
        <Route path="/pricing/invoice" element={<CarePassInvoice />} />
        <Route path="/caregivers/:caregiverId" element={<PublicCaregiverProfile />} />
        <Route path="/login" element={<Login />} />
        <Route path="/admin/login" element={<Login />} />
        {renderCustomerRoutes()}
        {renderCaregiverRoutes()}
        {renderCareManagerRoutes()}

        <Route path="/admin" element={<OpsLayout />}>
          <Route index element={adminModule("dashboard", <OpsDashboardHome />)} />
          <Route path="bookings" element={adminModule("bookings", <AdminBookingManagement />)} />
          <Route path="shifts" element={adminModule("bookings", <AdminShiftManagement />)} />
          <Route path="care-agreements" element={adminModule("bookings", <AdminCareAgreements />)} />
          <Route path="matchmaking" element={adminModule("matchmaking", <AdminMatchmaking />)} />
          <Route path="matchmaking/:bookingId" element={adminModule("matchmaking", <AdminRequirementDetails />)} />
          <Route path="customers" element={adminModule("customers", <AdminCustomerManagement />)} />
          <Route path="caregivers" element={adminModule("caregivers", <OpsCaregiverProfiles />)} />
          <Route
            path="caregivers/:caregiverId/profile"
            element={adminModule("caregivers", <AdminCaregiverProfileEditor />)}
          />
          <Route path="attendants" element={adminModule("caregivers", <AdminAttendantManagement />)} />
          <Route path="nurses" element={adminModule("caregivers", <AdminNurseManagement />)} />
          <Route path="physiotherapists" element={adminModule("caregivers", <AdminPhysiotherapistManagement />)} />
          <Route path="care-managers" element={adminModule("care_managers", <AdminCareManagerManagement />)} />
          <Route path="verification" element={adminModule("verification", <OpsVerificationQueue />)} />
          <Route path="police-verification" element={adminModule("verification", <OpsPoliceRecordVerification />)} />
          <Route path="carehub" element={adminModule("verification", <OpsCareHubConsole />)} />
          <Route path="subscriptions" element={adminModule("subscriptions", <AdminSubscriptions />)} />
          <Route path="ncid" element={adminModule("npid", <AdminNpidManagement />)} />
          <Route path="npid" element={<Navigate to="/admin/ncid" replace />} />
          <Route path="complaints" element={adminModule("complaints", <AdminComplaintsTrust />)} />
          <Route path="conflict-resolution" element={<Navigate to="/admin/complaints" replace />} />
          <Route path="payments" element={adminModule("payments", <AdminPaymentsFinance />)} />
          <Route path="trust-safety" element={adminModule("trust_safety", <OpsSopModulePage module="trust-safety" />)} />
          <Route path="crms" element={adminModule("trust_safety", <OpsCrmsPage />)} />
          <Route path="reports/weekly" element={adminModule("reports", <AdminWeeklyReport />)} />
          <Route path="reports" element={adminModule("reports", <AdminReportsAnalytics />)} />
          <Route path="lead-crm" element={adminModule("reports", <OpsLeadCrmPage />)} />
          <Route path="tickets" element={adminModule("complaints", <AdminSupportTickets />)} />
          <Route path="daily-ops" element={adminModule("daily_ops", <AdminDailyOperationsReport />)} />
          <Route path="roles" element={adminModule("admin_roles", <AdminUserRoleManagement />)} />
          <Route path="audit" element={adminModule("admin_roles", <AdminAuditCenter />)} />
          <Route path="integrations" element={adminModule("settings", <AdminIntegrations />)} />
          <Route path="permissions" element={<Navigate to="/admin/roles" replace />} />
          <Route path="notification-templates" element={adminModule("notifications", <OpsSopModulePage module="notifications" />)} />
          <Route path="features" element={adminModule("settings", <AdminFeatureManagement />)} />
          <Route path="platform" element={adminModule("settings", <AdminPlatformConfiguration />)} />
          <Route
            path="nursing-procedures"
            element={adminModule("settings", <AdminNursingProcedureCatalog />)}
          />
          <Route
            path="physio-services"
            element={adminModule("settings", <AdminPhysioServiceCatalog />)}
          />
          <Route path="settings" element={adminModule("settings", <AdminSystemSettings />)} />
          <Route path="platform-controls" element={adminModule("settings", <OpsPlatformSettings />)} />
          <Route path="call-management" element={adminModule("settings", <OpsCallManagement />)} />
          <Route path="booking-ops" element={adminModule("settings", <OpsBookingControls />)} />
          <Route path="call-timing" element={<Navigate to="/admin/call-management" replace />} />
          <Route path="notifications" element={adminModule("notifications", <NotificationsPage role="ADMIN" />)} />
          <Route path="messenger" element={adminModule("notifications", <MessengerPage role="ADMIN" />)} />
          {Object.keys(LEGACY_ADMIN_REDIRECTS).map((legacyPath) => {
            const sub = legacyPath.replace("/admin/", "");
            return <Route key={legacyPath} path={sub} element={<OpsLegacyRedirect />} />;
          })}
        </Route>
        <Route path="/ops/*" element={<LegacyOpsRedirect />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default AppRoutes;
